import { useMemo } from 'react';
import { ColumnDef, createColumnHelper, SortingFn } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { FamilyDto, FamilyWithSurveyDto, SurveyStatusColorEnum, SurveyStatusEnum } from '@families-link/shared';
import { Avatar, Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { tableCellTypographyOverflowStyles } from '../../../_core/styles';
import { FamilyAttachTableHandlers } from '../../components/family-attach-table/family-attach-table.component';

const helper = createColumnHelper<FamilyWithSurveyDto & { createdAt: string; updatedAt: string }>();

export function useFamilyAttachTableColumns(
	handlers: FamilyAttachTableHandlers,
	selectedRowId: string | null
): ColumnDef<FamilyWithSurveyDto, string>[] {
	const { onRowSelect } = handlers;

	const [t] = useTranslation();

	const fullNameSort: SortingFn<any> = (rowA, rowB, columnId) => {
		const a = rowA.getValue<FamilyDto>(columnId)?.fullName || '';
		const b = rowB.getValue<FamilyDto>(columnId)?.fullName || '';

		const collator = new Intl.Collator(undefined, {
			numeric: true,
			sensitivity: 'base',
		});

		return collator.compare(a, b);
	};

	const phoneNumberSort: SortingFn<any> = (rowA, rowB, columnId) => {
		const a = rowA.getValue<string>(columnId) || '';
		const b = rowB.getValue<string>(columnId) || '';

		const collator = new Intl.Collator(undefined, {
			numeric: true,
			sensitivity: 'base',
		});

		return collator.compare(a, b);
	};

	const surveyStatusSort: SortingFn<any> = (rowA, rowB, columnId) => {
		const a = rowA.getValue<string>(columnId) || '';
		const b = rowB.getValue<string>(columnId) || '';

		const collator = new Intl.Collator(undefined, {
			numeric: true,
		});

		return collator.compare(a, b);
	};

	const result = useMemo(
		() => [
			helper.accessor('family._id', {
				header: '',
				meta: {
					flex: 0.2,
				},
				enableSorting: false,
				cell: ({ getValue, row }) => {
					const familyId = getValue();
					const surveyStatus = row.original.surveyStatus;

					return (
						<Box
							key={familyId}
							sx={{
								position: 'relative',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								width: '100%',
								height: '100%',
							}}
						>
							<Radio
								checked={selectedRowId === familyId}
								disabled={surveyStatus === SurveyStatusEnum.Completed}
								onClick={() => {
									onRowSelect(familyId);
								}}
								sx={{ '&.MuiButtonBase-root': { padding: '5px' } }}
							/>
						</Box>
					);
				},
			}) as ColumnDef<FamilyWithSurveyDto, string>,
			helper.accessor('family', {
				header: t('families.columns.full-name'),
				meta: {
					flex: 1,
					useCustomOverflowContainer: true,
				},
				enableSorting: true,
				sortingFn: fullNameSort,
				cell: ({ getValue, row }) => {
					const familyId = getValue()._id;
					const imageUrl = getValue().imageUrl;
					const fullName = getValue().fullName;
					const surveyStatus = row.original.surveyStatus;

					return (
						<Box key={familyId} sx={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
							<Avatar src={imageUrl} alt={fullName} style={{ width: 24, height: 24, borderRadius: '6px' }} />
							<Typography data-overflow="true" sx={mergeSx({ fontWeight: 500 }, tableCellTypographyOverflowStyles)}>
								{fullName}
							</Typography>
							{surveyStatus === SurveyStatusEnum.Completed ? (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										backgroundColor: 'rgba(255, 255, 255, 0.7)',
										pointerEvents: 'none',
										zIndex: 1,
									}}
								/>
							) : null}
						</Box>
					);
				},
			}) as ColumnDef<FamilyWithSurveyDto, string>,
			helper.accessor('family.phoneNumber', {
				header: t('families.columns.phone-number'),
				meta: {
					flex: 1,
					useCustomOverflowContainer: true,
				},
				enableSorting: true,
				sortingFn: phoneNumberSort,
				cell: ({ getValue, row }) => {
					const phoneNumber = getValue();
					const surveyStatus = row.original.surveyStatus;

					return (
						<Box key={phoneNumber} sx={{ position: 'relative' }}>
							<Typography data-overflow="true" sx={tableCellTypographyOverflowStyles}>
								{phoneNumber}
							</Typography>

							{surveyStatus === SurveyStatusEnum.Completed ? (
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										bottom: 0,
										backgroundColor: 'rgba(255, 255, 255, 0.7)',
										pointerEvents: 'none',
										zIndex: 1,
									}}
								/>
							) : null}
						</Box>
					);
				},
			}) as ColumnDef<FamilyWithSurveyDto, string>,
			helper.accessor('surveyStatus', {
				header: t('engagements.columns.survey-status'),
				meta: {
					flex: 1,
					useCustomOverflowContainer: true,
				},
				enableSorting: true,
				sortingFn: surveyStatusSort,
				cell: ({ getValue, row }) => (
					<Box
						key={row.original.family._id}
						sx={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}
					>
						<Box
							sx={{
								height: 10,
								width: 10,
								borderRadius: '50%',
								backgroundColor: SurveyStatusColorEnum[getValue()],
								flexShrink: 0,
							}}
						></Box>
						<Typography data-overflow="true" variant="body2" sx={tableCellTypographyOverflowStyles}>
							{getValue()}
						</Typography>
						{getValue() === SurveyStatusEnum.Completed ? (
							<Box
								sx={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									backgroundColor: 'rgba(255, 255, 255, 0.7)',
									pointerEvents: 'none',
									zIndex: 1,
								}}
							/>
						) : null}
					</Box>
				),
			}) as ColumnDef<FamilyWithSurveyDto, string>,
		],
		[t, onRowSelect, selectedRowId]
	);

	return result;
}
